import React, { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { cn } from '../ui/Button';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';
import { AiChatPanel } from '../ai/AiChatPanel';
import { useElementSelector } from '../../hooks/useElementSelector';
import { ElementSelectorOverlay } from '../ai/ElementSelectorOverlay';
import { useWorkspaces } from '../../hooks/useWorkspaces';
import { ThemeContext, useThemeState } from '../../hooks/useTheme';
import { Plus, Sparkles, X, Cloud, BarChart2, TrendingUp, Zap } from 'lucide-react';
import type { Widget } from '../../hooks/useWorkspaces';
const VIEW_CONTEXT_MAP: Record<string, {
  name: string;
  description: string;
}> = {
  '/': {
    name: 'Home',
    description: 'The user is on the Overview/Home page with the Athena AI chat interface for energy trading intelligence. This is the main landing page.'
  },
  '/workspaces': {
    name: 'Workspaces Overview',
    description: 'The user is on their Workspaces overview page showing all their workspaces as cards. They can create, pin, favorite, and delete workspaces.'
  },
  '/workspaces': {
    name: 'Workspaces',
    description: 'The user is on their Workspaces dashboard with draggable widgets: wind forecast charts, interactive weather maps, and AI text blocks. Reference the visible widgets when answering.'
  },
  '/map': {
    name: 'Full-Screen Map',
    description: 'The user is viewing the full-screen interactive weather map of Europe. Visible controls: overlay selector (Air Temperature 2m, Air Pressure Sea Level, Geopotential Height 500hPa, Surface Solar Radiation), weather layer toggles (Temperature, Air Pressure, Cloud Cover, Precipitation, Wind Speed), Points of Interest panel (Wind Farms, Solar Farms, Oil & Gas, Ports, Airports, Nuclear Plants, Hydro, LNG Terminals, Interconnectors, Conflict Zones), model selector (EPT-2, EC-IFS, GFS), and a timeline playback control. The map shows live OpenWeatherMap tile overlays. Answer questions about what they see on the map.'
  },
  '/stations': {
    name: 'Weather Stations',
    description: 'The user is on the Weather Stations monitoring page. They can see station observation data, filters by country/parameter/status, and forecast accuracy benchmarks comparing model predictions against actual station measurements.'
  },
  '/forecast': {
    name: 'Forecast Models',
    description: 'The user is on the Forecast model comparison page. They can see model cards for EC AIFS, EC IFS, EPT-2, and GFS with settings for parameters (wind speed, temperature, solar radiation), time horizons (D+0 to D+7), and regional coverage. They can compare model skill scores and run configurations.'
  },
  '/energy': {
    name: 'Energy Market Aggregates',
    description: 'The user is on the Energy Market Aggregates page showing ENTSOE data: day-ahead prices (€/MWh) for DE, FR, NL, NO2, GB; generation mix by source (wind, solar, nuclear, hydro, gas); cross-border interconnector flows; and load forecasts. Data updates in near-real-time. Reference specific markets and values when answering.'
  },
  '/intraday': {
    name: 'Intraday Trader Dashboard',
    description: 'The user is on the Intraday Trader dashboard. They can see: (1) A live news ticker with market alerts. (2) Three traffic light indicators showing Solar State (GREEN, 24.5 GW, +2.4%), Wind State (RED, 12.8 GW, -1.8% — model disagreement flagged), and Demand State (YELLOW, 68.2 GW, +2.4%). (3) A market summary showing German wind deviating -2.1 GW below forecast due to North Sea conditions, solar stable, demand elevated +1.5%. (4) Model picker with EPT-2, EC-IFS, GFS active. (5) Stacked sparkline charts for Wind Generation, Solar Generation, and Temperature comparing EPT-2 vs EC-IFS vs GFS over 24h. (6) A deviation heatmap showing model spread by hour (24h × 5 models). (7) A live signals feed with recent alerts. The Wind traffic light is RED — this is the primary alert requiring attention.'
  },
  '/notifications': {
    name: 'Notifications',
    description: 'The user is on the Notifications page viewing alerts, model updates, and market signals. They can filter by severity or category and mark items as read.'
  },
  '/overview': {
    name: 'Home',
    description: 'The user is on the Overview/Home page with the Athena AI chat interface for energy trading intelligence. This is the main landing page.'
  }
};
const WORKSPACE_TEMPLATES: {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  widgets: Widget[];
  counter: number;
}[] = [{
  id: 'meteorologist',
  name: 'Meteorologist',
  description: 'Weather analysis with maps, charts, and station data',
  icon: <Cloud size={18} className="text-[#EC4899]" data-id="element-739" />,
  counter: 5,
  widgets: [{
    i: 'map-1',
    x: 0,
    y: 0,
    w: 8,
    h: 6,
    type: 'map',
    config: {
      overlay: 'Geopotential Height 500hPa'
    }
  }, {
    i: 'chart-1',
    x: 8,
    y: 0,
    w: 4,
    h: 3,
    type: 'chart'
  }, {
    i: 'chart-2',
    x: 8,
    y: 3,
    w: 4,
    h: 3,
    type: 'chart'
  }, {
    i: 'table-1',
    x: 0,
    y: 6,
    w: 6,
    h: 4,
    type: 'table'
  }, {
    i: 'chart-3',
    x: 6,
    y: 6,
    w: 6,
    h: 4,
    type: 'chart'
  }]
}, {
  id: 'short-term-trader',
  name: 'Short Term Trader',
  description: 'Intraday price signals, demand forecasts, and model runs',
  icon: <Zap size={18} className="text-[#EC4899]" data-id="element-740" />,
  counter: 5,
  widgets: [{
    i: 'chart-1',
    x: 0,
    y: 0,
    w: 4,
    h: 4,
    type: 'chart'
  }, {
    i: 'chart-2',
    x: 4,
    y: 0,
    w: 8,
    h: 4,
    type: 'chart'
  }, {
    i: 'table-1',
    x: 0,
    y: 4,
    w: 6,
    h: 4,
    type: 'table'
  }, {
    i: 'chart-3',
    x: 6,
    y: 4,
    w: 6,
    h: 4,
    type: 'chart'
  }, {
    i: 'map-1',
    x: 0,
    y: 8,
    w: 12,
    h: 5,
    type: 'map'
  }]
}, {
  id: 'energy-analyst',
  name: 'Energy Analyst',
  description: 'Long-range forecasts, model comparisons, and reporting',
  icon: <BarChart2 size={18} className="text-[#EC4899]" data-id="element-741" />,
  counter: 5,
  widgets: [{
    i: 'chart-1',
    x: 0,
    y: 0,
    w: 6,
    h: 4,
    type: 'chart'
  }, {
    i: 'chart-2',
    x: 6,
    y: 0,
    w: 6,
    h: 4,
    type: 'chart'
  }, {
    i: 'table-1',
    x: 0,
    y: 4,
    w: 8,
    h: 4,
    type: 'table'
  }, {
    i: 'text-1',
    x: 8,
    y: 4,
    w: 4,
    h: 4,
    type: 'text',
    config: {
      prompt: 'Add your analysis notes here...'
    }
  }, {
    i: 'map-1',
    x: 0,
    y: 8,
    w: 12,
    h: 5,
    type: 'map',
    config: {
      overlay: 'Geopotential Height 500hPa'
    }
  }]
}, {
  id: 'energy-trader',
  name: 'Energy Trader',
  description: 'Multi-market price monitoring, generation mix, and live positions',
  icon: <TrendingUp size={18} className="text-[#EC4899]" data-id="element-742" />,
  counter: 7,
  widgets: [{
    i: 'chart-1',
    x: 0,
    y: 0,
    w: 5,
    h: 4,
    type: 'chart'
  }, {
    i: 'chart-2',
    x: 5,
    y: 0,
    w: 7,
    h: 4,
    type: 'chart'
  }, {
    i: 'table-1',
    x: 0,
    y: 4,
    w: 6,
    h: 4,
    type: 'table'
  }, {
    i: 'chart-3',
    x: 6,
    y: 4,
    w: 6,
    h: 4,
    type: 'chart'
  }, {
    i: 'map-1',
    x: 0,
    y: 8,
    w: 8,
    h: 5,
    type: 'map',
    config: {
      overlay: 'Geopotential Height 500hPa'
    }
  }, {
    i: 'text-1',
    x: 8,
    y: 8,
    w: 4,
    h: 5,
    type: 'text',
    config: {
      prompt: 'Log positions and market observations here...'
    }
  }, {
    i: 'chart-4',
    x: 0,
    y: 13,
    w: 12,
    h: 4,
    type: 'chart'
  }]
}];
export function AppLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    createWorkspace,
    updateWidgetsById,
    renameWorkspace,
    workspaces
  } = useWorkspaces();
  const [isAiOpen, setIsAiOpen] = useState(false);
  const [aiMode, setAiMode] = useState<'docked' | 'floating'>('docked');
  const [dockedWidth, setDockedWidth] = useState(380);
  const [isAiResizing, setIsAiResizing] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createModalStep, setCreateModalStep] = useState<'choose' | 'template'>('choose');
  const themeState = useThemeState();
  const {
    isSelecting,
    hoveredRect,
    selectedElement,
    startSelecting,
    stopSelecting,
    clearSelection
  } = useElementSelector();
  const isDocked = isAiOpen && aiMode === 'docked';
  const viewContext = VIEW_CONTEXT_MAP[location.pathname] || {
    name: 'Unknown',
    description: 'The user is on an unrecognized page.'
  };
  const handleOpenCreateModal = () => {
    setCreateModalStep('choose');
    setCreateModalOpen(true);
  };
  const handleNewBlank = () => {
    setCreateModalOpen(false);
    const newId = createWorkspace();
    navigate(`/workspace/${newId}`, {
      state: {
        renaming: true
      }
    });
  };
  const handleUseTemplate = (template: (typeof WORKSPACE_TEMPLATES)[number]) => {
    setCreateModalOpen(false);
    if (template.id === 'short-term-trader') {
      try {
        const existing = JSON.parse(localStorage.getItem('jua-pinned-pages') || '[]');
        if (!existing.includes('intraday')) {
          localStorage.setItem('jua-pinned-pages', JSON.stringify([...existing, 'intraday']));
        }
      } catch {}
      navigate('/intraday');
      return;
    }
    const baseName = template.name;
    const existingNames = new Set(workspaces.map(w => w.name));
    let uniqueName = baseName;
    let suffix = 2;
    while (existingNames.has(uniqueName)) {
      uniqueName = `${baseName} ${suffix}`;
      suffix++;
    }
    const newId = createWorkspace();
    setTimeout(() => {
      updateWidgetsById(newId, template.widgets, template.counter);
      renameWorkspace(newId, uniqueName);
      navigate(`/workspace/${newId}`);
    }, 0);
  };
  // Handle ESC key to cancel selection
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isSelecting) {
        stopSelecting();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isSelecting, stopSelecting]);
  // Open AI panel when an element is selected
  useEffect(() => {
    if (selectedElement) {
      setIsAiOpen(true);
    }
  }, [selectedElement]);
  // Listen for "Edit with AI" events dispatched from widgets
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (!detail) return;
      // Reconstruct a DOMRect-like object
      const rect = {
        top: detail.rect.top,
        left: detail.rect.left,
        right: detail.rect.right,
        bottom: detail.rect.bottom,
        width: detail.rect.width,
        height: detail.rect.height,
        x: detail.rect.x,
        y: detail.rect.y,
        toJSON: () => detail.rect
      } as DOMRect;
      clearSelection();
      // Small delay so clearSelection settles before setting new element
      setTimeout(() => {
        // Directly set selected element via the selector hook's internal setter
        // We trigger it by simulating what startSelecting + click does:
        // Use a workaround: dispatch a synthetic selectedElement via a ref
        setIsAiOpen(true);
        (window as any).__juaSetSelectedElement?.({
          description: detail.description,
          textContent: detail.textContent,
          tagName: detail.tagName,
          rect,
          elementType: detail.elementType
        });
      }, 50);
    };
    window.addEventListener('jua:edit-with-ai', handler);
    return () => window.removeEventListener('jua:edit-with-ai', handler);
  }, [clearSelection]);
  const isLight = themeState.theme === 'light';
  return <ThemeContext.Provider value={themeState} data-id="element-743">
      <div className="app-root min-h-screen font-sans selection:bg-[#EC4899]/30 selection:text-[#EC4899]" style={{
      color: isLight ? '#111827' : '#f1f5f9',
      position: 'relative'
    }} data-id="element-744">
        {/* ── Light mode background: fixed gradient + animated orbs ── */}
        {isLight && <div className="fixed inset-0 pointer-events-none" style={{
        zIndex: 0,
        background: 'linear-gradient(to bottom right, #fefafc, #fdfaff, #f5feff)'
      }} data-id="element-745">
            {/* Top-right orb */}
            <div className="absolute animate-pulse" style={{
          top: '-200px',
          right: '-200px',
          width: '800px',
          height: '800px',
          borderRadius: '50%',
          background: 'radial-gradient(ellipse at center, rgba(249,168,212,0.15), rgba(216,180,254,0.15), transparent 70%)',
          filter: 'blur(60px)'
        }} data-id="element-746" />
            {/* Bottom-left orb */}
            <div className="absolute animate-pulse" style={{
          bottom: '-150px',
          left: '-150px',
          width: '600px',
          height: '600px',
          borderRadius: '50%',
          background: 'radial-gradient(ellipse at center, rgba(103,232,249,0.10), rgba(216,180,254,0.10), transparent 70%)',
          filter: 'blur(60px)',
          animationDelay: '1s'
        }} data-id="element-747" />
          </div>}

        {/* ── Dark mode background ── */}
        {!isLight && <div className="fixed inset-0 pointer-events-none" style={{
        zIndex: 0,
        background: '#0B1120'
      }} data-id="element-748" />}

        {/* All content sits above the background */}
        <div className="relative" style={{
        zIndex: 1
      }} data-id="element-749">
          <Sidebar isOpen={isSidebarOpen} onToggle={() => setIsSidebarOpen(!isSidebarOpen)} data-id="element-750" />
          {isSidebarOpen && <div className="fixed inset-0 z-30 bg-black/50 lg:hidden" onClick={() => setIsSidebarOpen(false)} data-id="element-751" />}
          <TopBar onToggleAi={() => setIsAiOpen(!isAiOpen)} onOpenAi={() => setIsAiOpen(true)} isAiOpen={isDocked} dockedWidth={dockedWidth} isSidebarOpen={isSidebarOpen} onToggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} onCreateWorkspace={handleOpenCreateModal} data-id="element-752" />
          <main className={cn('pt-16 min-h-screen', !isAiResizing && 'transition-all duration-300 ease-in-out')} style={{
          paddingLeft: isSidebarOpen ? '240px' : '0',
          marginRight: isDocked ? `${dockedWidth}px` : '0',
          backgroundColor: 'transparent'
        }} data-id="element-753">
            <Outlet data-id="element-754" />
          </main>
          <ElementSelectorOverlay isSelecting={isSelecting} hoveredRect={hoveredRect} data-id="element-755" />
          <AiChatPanel isOpen={isAiOpen} mode={aiMode} onClose={() => setIsAiOpen(false)} onModeChange={setAiMode} onDockedWidthChange={setDockedWidth} onDockedResizingChange={setIsAiResizing} onStartSelection={() => {
          startSelecting();
        }} selectedElement={selectedElement} onClearSelection={clearSelection} viewContext={viewContext} data-id="element-756" />

          {/* Global New Workspace Modal */}
          {createModalOpen && <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => setCreateModalOpen(false)} data-id="element-757">
              <div className="w-full max-w-md rounded-2xl border border-white/[0.08] shadow-2xl shadow-black/60 overflow-hidden" style={{
            background: 'rgba(15, 23, 42, 0.97)',
            backdropFilter: 'blur(40px)'
          }} onClick={e => e.stopPropagation()} data-id="element-758">
                <div className="flex items-center justify-between px-6 py-4 border-b border-white/[0.06]" data-id="element-759">
                  <div data-id="element-760">
                    <h2 className="text-base font-semibold text-white" data-id="element-761">
                      New Workspace
                    </h2>
                    <p className="text-xs text-slate-400 mt-0.5" data-id="element-762">
                      {createModalStep === 'choose' ? 'How would you like to start?' : 'Choose a template'}
                    </p>
                  </div>
                  <button onClick={() => setCreateModalOpen(false)} className="p-1.5 text-white/40 hover:text-white hover:bg-white/[0.08] rounded-lg transition-colors" data-id="element-763">
                    <X size={16} data-id="element-764" />
                  </button>
                </div>
                <div className="p-5" data-id="element-765">
                  {createModalStep === 'choose' ? <div className="flex gap-3" data-id="element-766">
                      <button onClick={handleNewBlank} className="flex-1 flex flex-col items-center gap-3 p-5 rounded-xl border border-white/[0.08] hover:border-[#EC4899]/40 bg-white/[0.03] hover:bg-[#EC4899]/[0.06] transition-all group text-center" data-id="element-767">
                        <div className="w-12 h-12 rounded-xl bg-slate-800 group-hover:bg-[#EC4899]/10 border border-white/[0.06] group-hover:border-[#EC4899]/20 flex items-center justify-center transition-all" data-id="element-768">
                          <Plus size={22} className="text-slate-400 group-hover:text-[#EC4899] transition-colors" data-id="element-769" />
                        </div>
                        <div data-id="element-770">
                          <div className="text-sm font-semibold text-white mb-1" data-id="element-771">
                            Blank
                          </div>
                          <div className="text-[11px] text-slate-400 leading-relaxed" data-id="element-772">
                            Start from scratch with an empty canvas
                          </div>
                        </div>
                      </button>
                      <button onClick={() => setCreateModalStep('template')} className="flex-1 flex flex-col items-center gap-3 p-5 rounded-xl border border-white/[0.08] hover:border-[#EC4899]/40 bg-white/[0.03] hover:bg-[#EC4899]/[0.06] transition-all group text-center" data-id="element-773">
                        <div className="w-12 h-12 rounded-xl bg-slate-800 group-hover:bg-[#EC4899]/10 border border-white/[0.06] group-hover:border-[#EC4899]/20 flex items-center justify-center transition-all" data-id="element-774">
                          <Sparkles size={20} className="text-slate-400 group-hover:text-[#EC4899] transition-colors" data-id="element-775" />
                        </div>
                        <div data-id="element-776">
                          <div className="text-sm font-semibold text-white mb-1" data-id="element-777">
                            Use Template
                          </div>
                          <div className="text-[11px] text-slate-400 leading-relaxed" data-id="element-778">
                            Start with a pre-built widget layout
                          </div>
                        </div>
                      </button>
                    </div> : <div className="space-y-2" data-id="element-779">
                      {WORKSPACE_TEMPLATES.map(tpl => <button key={tpl.id} onClick={() => handleUseTemplate(tpl)} className="w-full flex items-center gap-4 px-4 py-3.5 rounded-xl border border-white/[0.06] hover:border-[#EC4899]/30 bg-white/[0.02] hover:bg-[#EC4899]/[0.05] transition-all group text-left" data-id="element-780">
                          <div className="w-9 h-9 rounded-lg bg-[#EC4899]/10 border border-[#EC4899]/20 flex items-center justify-center flex-shrink-0" data-id="element-781">
                            {tpl.icon}
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-782">
                            <div className="text-sm font-semibold text-white group-hover:text-[#EC4899] transition-colors" data-id="element-783">
                              {tpl.name}
                            </div>
                            <div className="text-[11px] text-slate-400 mt-0.5" data-id="element-784">
                              {tpl.description}
                            </div>
                          </div>
                          <div className="text-[10px] text-slate-500 flex-shrink-0" data-id="element-785">
                            {tpl.widgets.length} widget
                            {tpl.widgets.length !== 1 ? 's' : ''}
                          </div>
                        </button>)}
                      <button onClick={() => setCreateModalStep('choose')} className="w-full mt-1 py-2 text-xs text-slate-500 hover:text-slate-300 transition-colors" data-id="element-786">
                        ← Back
                      </button>
                    </div>}
                </div>
              </div>
            </div>}
        </div>
      </div>
    </ThemeContext.Provider>;
}